import clsx from 'clsx';
import { useFilterContext } from '../FilterV2Context';
import BaseFilter, { BaseFilterParams } from './BaseFilter';
import { useRef } from 'react';
import { DateFilterCondition } from '../filterV2.types';
import { getDateConditionLabel } from '../utils';
import { ArrowRightIcon } from '@heroicons/react/20/solid';

export type DateFilterProps = BaseFilterParams;

/**
 * Filter option that contains various date conditions.
 *
 * *Use Case*: Filtering a date value with common number comparators.
 *
 * *Example*: Worksheet updated at is between before a date.
 *
 * **NOTE** Values will be converted to Epoch.
 *
 * @param props DateFilterProps
 */
function DateFilter({ id, label }: DateFilterProps): JSX.Element {
    const ctx = useFilterContext();
    const inputRef = useRef<HTMLInputElement>(null);

    const { condition, values } = ctx.getDraft(id, 'date');

    const isActive = condition === 'btw' ? typeof values[0] === 'number' && typeof values[1] === 'number' : typeof values[0] === 'number';

    function handleConditionChange(e: React.ChangeEvent<HTMLSelectElement>): void {
        const newCondition = e.target.value as DateFilterCondition;
        const newValues = newCondition !== 'btw' && condition === 'btw' ? [values[0]] : values;
        ctx.updateValues('numeric', id, { condition: newCondition, values: newValues });
        inputRef.current?.focus();
    }

    function handleChange(index: number, value: number): void {
        const copy = [...values];

        if (isNaN(value)) {
            copy.splice(index, 1);
        } else {
            copy[index] = value;
        }

        ctx.updateValues('numeric', id, { values: copy });
    }

    return (
        <BaseFilter id={id} label={label} activeCount={isActive ? 1 : 0} key={id} onMenuClose={console.log}>
            <div className="flex items-center gap-2">
                <label className="text-sm text-gray-700">is</label>
                <select
                    className={clsx(
                        'bg-white dark:bg-white/[0.12] text-xs  rounded border-gray-300 dark:border-white/[0.05] py-1 px-2 outline-none focus:ring-0 cursor-pointer overflow-hidden text-gray-700 dark:text-gray-200 w-[100px]'
                    )}
                    defaultValue={condition}
                    onChange={handleConditionChange}
                >
                    <option value="eq">{getDateConditionLabel('eq')}</option>
                    <option value="lt">{getDateConditionLabel('lt')}</option>
                    <option value="gt">{getDateConditionLabel('gt')}</option>
                    <option value="btw">{getDateConditionLabel('btw')}</option>
                </select>
                <ArrowRightIcon className="h-5 w-5 text-indigo-600 shrink-0" />
                <input
                    ref={inputRef}
                    autoFocus
                    type="date"
                    max="9999-12-31"
                    value={values[0] ? new Date(values[0]).toISOString().substring(0, 10) : ''}
                    onChange={(e): void => handleChange(0, e.target.valueAsNumber)}
                    className="col-span-2 outline-none text-xs text-gray-700 rounded border border-gray-200 py-1 px-2 w-[120px] focus:ring-indigo-600"
                />
            </div>
            {condition === 'btw' && (
                <>
                    <div className="mr-0 ml-auto w-fit mt-2 flex flex-col items-center">
                        <label className="text-sm text-gray-700 text-right mb-2">and</label>
                        <input
                            type="date"
                            value={values[1] ? new Date(values[1]).toISOString().substring(0, 10) : ''}
                            onChange={(e): void => handleChange(1, e.target.valueAsNumber)}
                            className="col-span-2 outline-none text-xs text-gray-700 rounded border border-gray-200 py-1 px-2 w-[120px] focus:ring-indigo-600"
                        />
                    </div>
                </>
            )}
        </BaseFilter>
    );
}

export default DateFilter;
